import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Public,
  Flag,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  Notifications,
  Settings,
  FilterList,
  Download,
  Refresh,
  Search,
  Add,
  Edit,
  MoreVert,
  Timeline,
  Analytics,
  Assignment,
  Person,
  DirectionsCar,
  Security,
  Business,
  Gavel,
  LocationOn,
  Language,
  Policy,
  VerifiedUser,
  Update,
  NotificationsActive,
  CompareArrows,
  ExpandMore,
  Assessment,
  Map,
  PieChart,
  BarChart,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, BarChart as RechartsBarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

interface RegionalCompliance {
  region: string;
  country: string;
  flag: string;
  overallCompliance: number;
  trend: number;
  trendDirection: 'up' | 'down';
  totalHosts: number;
  activeHosts: number;
  documents: {
    identity: { rate: number; required: boolean; localName: string; };
    license: { rate: number; required: boolean; localName: string; };
    vehicle: { rate: number; required: boolean; localName: string; };
    insurance: { rate: number; required: boolean; localName: string; };
    businessLicense: { rate: number; required: boolean; localName: string; };
    taxClearance: { rate: number; required: boolean; localName: string; };
  };
  regulations: RegulatoryRequirement[];
  lastUpdated: string;
  nextAudit: string;
  complianceOfficer: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}

interface RegulatoryRequirement {
  id: string;
  title: string;
  description: string;
  type: 'mandatory' | 'recommended' | 'upcoming';
  implementationDeadline: string;
  complianceRate: number;
  affectedHosts: number;
  status: 'compliant' | 'partial' | 'non-compliant';
  authority: string;
  penalties: string;
  lastUpdated: string;
}

interface RegulatoryAlert {
  id: string;
  region: string;
  type: 'new-regulation' | 'deadline-approaching' | 'compliance-drop' | 'audit-required';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  deadline: string;
  actionRequired: string;
  responsibleTeam: string;
  status: 'active' | 'acknowledged' | 'resolved';
  createdAt: string;
}

interface CrossRegionComparison {
  metric: string;
  regions: {
    [key: string]: {
      value: number;
      rank: number;
      status: 'leading' | 'average' | 'lagging';
    };
  };
}

const RegionalCompliancePage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [regulationDialogOpen, setRegulationDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRegionDetails, setSelectedRegionDetails] = useState<RegionalCompliance | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<RegionalCompliance | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [newRegulation, setNewRegulation] = useState<{
    region: string;
    title: string;
    description: string;
    type: 'mandatory' | 'recommended' | 'upcoming';
    implementationDeadline: string;
    authority: string;
    penalties: string;
    affectedHosts: number;
  }>({
    region: '',
    title: '',
    description: '',
    type: 'mandatory',
    implementationDeadline: '',
    authority: '',
    penalties: '',
    affectedHosts: 0
  });

  // Sample data - in real implementation, this would come from APIs
  const regionalCompliance: RegionalCompliance[] = [
    {
      region: 'Lagos',
      country: 'Nigeria',
      flag: '🇳🇬',
      overallCompliance: 86.3,
      trend: 2.1,
      trendDirection: 'up',
      totalHosts: 1247,
      activeHosts: 1089,
      documents: {
        identity: { rate: 89.1, required: true, localName: 'National ID/Voter\'s Card' },
        license: { rate: 78.4, required: true, localName: 'Nigerian Driver\'s License' },
        vehicle: { rate: 88.7, required: true, localName: 'Vehicle Registration' },
        insurance: { rate: 85.2, required: true, localName: 'Third Party Insurance' },
        businessLicense: { rate: 72.3, required: true, localName: 'CAC Registration' },
        taxClearance: { rate: 68.9, required: true, localName: 'Tax Clearance Certificate' }
      },
      regulations: [
        {
          id: '1',
          title: 'Lagos State E-hailing Regulation 2024',
          description: 'Updated requirements for ride-sharing platforms',
          type: 'mandatory',
          implementationDeadline: '2024-03-01',
          complianceRate: 78.5,
          affectedHosts: 1247,
          status: 'partial',
          authority: 'Lagos State Transport Authority',
          penalties: 'N500,000 - N2,000,000 fine',
          lastUpdated: '2024-01-15'
        }
      ],
      lastUpdated: '2024-01-15T10:30:00Z',
      nextAudit: '2024-02-15T09:00:00Z',
      complianceOfficer: 'Adebayo Ogundimu',
      riskLevel: 'medium'
    },
    {
      region: 'Nairobi',
      country: 'Kenya',
      flag: '🇰🇪',
      overallCompliance: 87.7,
      trend: 1.4,
      trendDirection: 'up',
      totalHosts: 892,
      activeHosts: 804,
      documents: {
        identity: { rate: 91.2, required: true, localName: 'National ID/Passport' },
        license: { rate: 82.3, required: true, localName: 'Kenyan Driving License' },
        vehicle: { rate: 90.1, required: true, localName: 'Log Book' },
        insurance: { rate: 87.8, required: true, localName: 'Comprehensive Insurance' },
        businessLicense: { rate: 76.4, required: true, localName: 'Business Permit' },
        taxClearance: { rate: 74.2, required: false, localName: 'KRA PIN Certificate' }
      },
      regulations: [
        {
          id: '2',
          title: 'Kenya Digital Transport Act 2024',
          description: 'New digital platform licensing requirements',
          type: 'upcoming',
          implementationDeadline: '2024-06-01',
          complianceRate: 45.2,
          affectedHosts: 892,
          status: 'non-compliant',
          authority: 'Transport & Infrastructure Ministry',
          penalties: 'KES 100,000 - KES 500,000',
          lastUpdated: '2024-01-10'
        }
      ],
      lastUpdated: '2024-01-15T11:15:00Z',
      nextAudit: '2024-02-28T14:00:00Z',
      complianceOfficer: 'Grace Wanjiku',
      riskLevel: 'low'
    },
    {
      region: 'Accra',
      country: 'Ghana',
      flag: '🇬🇭',
      overallCompliance: 82.5,
      trend: -0.8,
      trendDirection: 'down',
      totalHosts: 634,
      activeHosts: 567,
      documents: {
        identity: { rate: 85.7, required: true, localName: 'Ghana Card/Passport' },
        license: { rate: 76.2, required: true, localName: 'DVLA License' },
        vehicle: { rate: 84.9, required: true, localName: 'Road Worthy Certificate' },
        insurance: { rate: 82.1, required: true, localName: 'Motor Insurance' },
        businessLicense: { rate: 69.8, required: false, localName: 'Business Operating Permit' },
        taxClearance: { rate: 71.3, required: false, localName: 'TIN Certificate' }
      },
      regulations: [
        {
          id: '3',
          title: 'Ghana Ride-sharing Guidelines 2024',
          description: 'Updated safety and operational requirements',
          type: 'mandatory',
          implementationDeadline: '2024-04-15',
          complianceRate: 82.5,
          affectedHosts: 634,
          status: 'compliant',
          authority: 'National Road Safety Authority',
          penalties: 'GHS 5,000 - GHS 20,000',
          lastUpdated: '2024-01-08'
        }
      ],
      lastUpdated: '2024-01-15T09:45:00Z',
      nextAudit: '2024-03-10T10:30:00Z',
      complianceOfficer: 'Kwame Asante',
      riskLevel: 'medium'
    }
  ];

  const regulatoryAlerts: RegulatoryAlert[] = [
    {
      id: '1',
      region: 'Lagos',
      type: 'deadline-approaching',
      severity: 'high',
      title: 'CAC Registration Deadline Approaching',
      description: 'Business license registration deadline is 30 days away. 342 hosts still need to complete registration.',
      deadline: '2024-03-01T23:59:59Z',
      actionRequired: 'Notify affected hosts and provide registration assistance',
      responsibleTeam: 'Lagos Compliance Team',
      status: 'active',
      createdAt: '2024-01-15T08:00:00Z'
    },
    {
      id: '2',
      region: 'Nairobi',
      type: 'new-regulation',
      severity: 'critical',
      title: 'New Digital Transport Act Published',
      description: 'Kenya has published new digital transport regulations affecting all ride-sharing platforms.',
      deadline: '2024-06-01T00:00:00Z',
      actionRequired: 'Review requirements and update compliance workflows',
      responsibleTeam: 'Kenya Regulatory Team',
      status: 'acknowledged',
      createdAt: '2024-01-10T14:30:00Z'
    },
    {
      id: '3',
      region: 'Accra',
      type: 'compliance-drop',
      severity: 'medium',
      title: 'Driver License Verification Rate Declining',
      description: 'Driver license verification rate has dropped to 76.2%, below the 80% target.',
      deadline: '2024-02-01T23:59:59Z',
      actionRequired: 'Increase verification outreach and support',
      responsibleTeam: 'Ghana Operations Team',
      status: 'active',
      createdAt: '2024-01-12T11:20:00Z'
    }
  ];

  const crossRegionData: CrossRegionComparison[] = [
    {
      metric: 'Overall Compliance',
      regions: {
        'Lagos': { value: 86.3, rank: 2, status: 'average' },
        'Nairobi': { value: 87.7, rank: 1, status: 'leading' },
        'Accra': { value: 82.5, rank: 3, status: 'lagging' }
      }
    },
    {
      metric: 'Identity Verification',
      regions: {
        'Lagos': { value: 89.1, rank: 2, status: 'average' },
        'Nairobi': { value: 91.2, rank: 1, status: 'leading' },
        'Accra': { value: 85.7, rank: 3, status: 'lagging' }
      }
    },
    {
      metric: 'License Verification',
      regions: {
        'Lagos': { value: 78.4, rank: 2, status: 'average' },
        'Nairobi': { value: 82.3, rank: 1, status: 'leading' },
        'Accra': { value: 76.2, rank: 3, status: 'lagging' }
      }
    }
  ];

  const complianceTrendData = [
    { month: 'Sep', Lagos: 84.2, Nairobi: 85.1, Accra: 83.8 },
    { month: 'Oct', Lagos: 85.1, Nairobi: 86.3, Accra: 83.2 },
    { month: 'Nov', Lagos: 85.8, Nairobi: 87.1, Accra: 82.9 },
    { month: 'Dec', Lagos: 86.3, Nairobi: 87.7, Accra: 82.5 },
  ];

  const documentComplianceData = [
    { name: 'Identity', Lagos: 89.1, Nairobi: 91.2, Accra: 85.7 },
    { name: 'License', Lagos: 78.4, Nairobi: 82.3, Accra: 76.2 },
    { name: 'Vehicle', Lagos: 88.7, Nairobi: 90.1, Accra: 84.9 },
    { name: 'Insurance', Lagos: 85.2, Nairobi: 87.8, Accra: 82.1 },
    { name: 'Business', Lagos: 72.3, Nairobi: 76.4, Accra: 69.8 },
  ];

  const getRiskColor = (level: string): string => {
    switch (level) {
      case 'low': return '#4CAF50';
      case 'medium': return '#FF9800';
      case 'high': return '#FF5722';
      case 'critical': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'compliant': return '#4CAF50';
      case 'partial': return '#FF9800';
      case 'non-compliant': return '#F44336';
      case 'leading': return '#4CAF50';
      case 'average': return '#FF9800';
      case 'lagging': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#F44336';
      case 'high': return '#FF5722';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredRegions = regionalCompliance.filter(region => {
    const matchesSearch = region.region.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         region.country.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || region.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  // Export Report Handler
  const handleExportReport = () => {
    if (!selectedRegionDetails) return;

    // Create report content
    const reportContent = `
REGIONAL COMPLIANCE REPORT
${selectedRegionDetails.flag} ${selectedRegionDetails.region}, ${selectedRegionDetails.country}
Generated: ${new Date().toLocaleString()}

========================================
COMPLIANCE OVERVIEW
========================================
Overall Compliance Rate: ${selectedRegionDetails.overallCompliance.toFixed(1)}%
Trend: ${selectedRegionDetails.trend > 0 ? '+' : ''}${selectedRegionDetails.trend}% (${selectedRegionDetails.trendDirection})
Risk Level: ${selectedRegionDetails.riskLevel.toUpperCase()}
Active Hosts: ${selectedRegionDetails.activeHosts.toLocaleString()} / ${selectedRegionDetails.totalHosts.toLocaleString()}

========================================
DOCUMENT COMPLIANCE BREAKDOWN
========================================
${Object.entries(selectedRegionDetails.documents).map(([key, doc]) =>
  `${doc.localName}: ${doc.rate.toFixed(1)}% ${doc.required ? '(Required)' : '(Optional)'}`
).join('\n')}

========================================
REGULATORY REQUIREMENTS
========================================
${selectedRegionDetails.regulations.map((reg, idx) => `
${idx + 1}. ${reg.title}
   Type: ${reg.type}
   Status: ${reg.status}
   Authority: ${reg.authority}
   Deadline: ${formatDate(reg.implementationDeadline)}
   Compliance Rate: ${reg.complianceRate.toFixed(1)}%
   Affected Hosts: ${reg.affectedHosts.toLocaleString()}
   Penalties: ${reg.penalties}
   Description: ${reg.description}
`).join('\n')}

========================================
MANAGEMENT INFORMATION
========================================
Compliance Officer: ${selectedRegionDetails.complianceOfficer}
Next Audit: ${formatDate(selectedRegionDetails.nextAudit)}
Last Updated: ${formatDate(selectedRegionDetails.lastUpdated)}

========================================
Report generated by PolarRide Compliance System
    `.trim();

    // Create blob and download
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedRegionDetails.region}_Compliance_Report_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Edit Details Handler
  const handleEditDetails = () => {
    if (!selectedRegionDetails) return;
    setEditFormData({ ...selectedRegionDetails });
    setEditDialogOpen(true);
  };

  // Save Edited Details Handler
  const handleSaveEditedDetails = () => {
    if (!editFormData) return;

    // In real implementation, this would call an API to update the data

    // Update the selected region details
    setSelectedRegionDetails(editFormData);

    // Close the edit dialog
    setEditDialogOpen(false);

    // Show success message (you could use a toast notification here)
    alert(`Successfully updated compliance details for ${editFormData.region}`);
  };

  // Refresh Handler
  const handleRefresh = async () => {
    setRefreshing(true);

    // Simulate API call to refresh data
    await new Promise(resolve => setTimeout(resolve, 1500));

    // In real implementation, this would fetch fresh data from the API

    setRefreshing(false);
    alert('Compliance data refreshed successfully!');
  };

  // Reset Add Regulation Form
  const resetRegulationForm = () => {
    setNewRegulation({
      region: '',
      title: '',
      description: '',
      type: 'mandatory',
      implementationDeadline: '',
      authority: '',
      penalties: '',
      affectedHosts: 0
    });
  };

  // Add Regulation Handler
  const handleAddRegulation = () => {
    // Validate form
    if (!newRegulation.region || !newRegulation.title || !newRegulation.description ||
        !newRegulation.implementationDeadline || !newRegulation.authority ||
        !newRegulation.penalties) {
      alert('Please fill in all required fields');
      return;
    }

    // In real implementation, this would call an API to save the new regulation

    // Show success message
    alert(`Successfully added regulation "${newRegulation.title}" for ${newRegulation.region}`);

    // Close dialog and reset form
    setRegulationDialogOpen(false);
    resetRegulationForm();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Regional Compliance
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Multi-region regulatory compliance monitoring and management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Region</InputLabel>
            <Select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
              <MenuItem value="all">All Regions</MenuItem>
              <MenuItem value="Lagos">🇳🇬 Lagos</MenuItem>
              <MenuItem value="Nairobi">🇰🇪 Nairobi</MenuItem>
              <MenuItem value="Accra">🇬🇭 Accra</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetRegulationForm();
              setRegulationDialogOpen(true);
            }}
          >
            Add Regulation
          </Button>
        </Box>
      </Box>

      {/* Regional Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {filteredRegions.map((region) => (
          <Grid item xs={12} md={4} key={region.region}>
            <Card sx={{ 
              borderLeft: `4px solid ${getRiskColor(region.riskLevel)}`,
              height: '100%'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h2" component="span">
                      {region.flag}
                    </Typography>
                    <Box>
                      <Typography variant="h6" component="div">
                        {region.region}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {region.country}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={region.riskLevel}
                    size="small"
                    sx={{
                      backgroundColor: getRiskColor(region.riskLevel) + '20',
                      color: getRiskColor(region.riskLevel),
                      textTransform: 'capitalize'
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" component="div">
                      {region.overallCompliance.toFixed(1)}%
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {region.trendDirection === 'up' ? (
                        <TrendingUp sx={{ color: '#4CAF50', fontSize: 20 }} />
                      ) : (
                        <TrendingDown sx={{ color: '#F44336', fontSize: 20 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: region.trendDirection === 'up' ? '#4CAF50' : '#F44336',
                          ml: 0.5
                        }}
                      >
                        {region.trend > 0 ? '+' : ''}{region.trend}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={region.overallCompliance}
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Overall Compliance Rate
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Active Hosts: {region.activeHosts.toLocaleString()} / {region.totalHosts.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Compliance Officer: {region.complianceOfficer}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Next Audit: {formatDate(region.nextAudit)}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  onClick={() => {
                    setSelectedRegionDetails(region);
                    setDetailsDialogOpen(true);
                  }}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Regulatory Alerts */}
      {regulatoryAlerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsActive />
              Regulatory Alerts Require Attention
            </Box>
          </AlertTitle>
          {regulatoryAlerts.filter(alert => alert.status === 'active').length} active regulatory alerts across regions.
          <Button size="small" variant="outlined" sx={{ ml: 2 }} onClick={() => setAlertDialogOpen(true)}>
            View All Alerts
          </Button>
        </Alert>
      )}

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Public />} label="Regional Overview" />
          <Tab icon={<BarChart />} label="Document Compliance" />
          <Tab icon={<Timeline />} label="Compliance Trends" />
          <Tab icon={<CompareArrows />} label="Cross-Region Comparison" />
          <Tab icon={<Policy />} label="Regulatory Requirements" />
          <Tab icon={<Warning />} label={`Alerts (${regulatoryAlerts.length})`} />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Compliance by Document Type */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Document Compliance by Region
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <RechartsBarChart data={documentComplianceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="Lagos" fill="#2196F3" name="Lagos" />
                    <Bar dataKey="Nairobi" fill="#4CAF50" name="Nairobi" />
                    <Bar dataKey="Accra" fill="#FF9800" name="Accra" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Regional Risk Assessment */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Assessment
                </Typography>
                <List>
                  {regionalCompliance.map((region) => (
                    <ListItem key={region.region} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Typography variant="h6">{region.flag}</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={region.region}
                        secondary={`${region.overallCompliance}% compliance`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={region.riskLevel}
                          size="small"
                          sx={{
                            backgroundColor: getRiskColor(region.riskLevel) + '20',
                            color: getRiskColor(region.riskLevel),
                            textTransform: 'capitalize'
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Grid container spacing={3}>
          {regionalCompliance.map((region) => (
            <Grid item xs={12} md={4} key={region.region}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6">{region.flag}</Typography>
                    <Typography variant="h6">{region.region}</Typography>
                  </Box>
                  
                  <List dense>
                    {Object.entries(region.documents).map(([key, doc]) => (
                      <ListItem key={key} sx={{ px: 0 }}>
                        <ListItemText
                          primary={doc.localName}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={doc.rate}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="body2">
                                {doc.rate.toFixed(1)}%
                              </Typography>
                              {doc.required && (
                                <Chip label="Required" size="small" color="error" />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Regional Compliance Trends
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={complianceTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip />
                <Line type="monotone" dataKey="Lagos" stroke="#2196F3" strokeWidth={3} name="Lagos" />
                <Line type="monotone" dataKey="Nairobi" stroke="#4CAF50" strokeWidth={3} name="Nairobi" />
                <Line type="monotone" dataKey="Accra" stroke="#FF9800" strokeWidth={3} name="Accra" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cross-Region Performance Comparison
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell>🇳🇬 Lagos</TableCell>
                    <TableCell>🇰🇪 Nairobi</TableCell>
                    <TableCell>🇬🇭 Accra</TableCell>
                    <TableCell>Best Performer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crossRegionData.map((comparison) => (
                    <TableRow key={comparison.metric}>
                      <TableCell fontWeight="medium">{comparison.metric}</TableCell>
                      {Object.entries(comparison.regions).map(([region, data]) => (
                        <TableCell key={region}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {data.value.toFixed(1)}%
                            </Typography>
                            <Chip
                              label={`#${data.rank}`}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(data.status) + '20',
                                color: getStatusColor(data.status)
                              }}
                            />
                          </Box>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Chip
                          label="Nairobi"
                          size="small"
                          color="success"
                          icon={<TrendingUp />}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 4 && (
        <Grid container spacing={3}>
          {regionalCompliance.map((region) => (
            <Grid item xs={12} key={region.region}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6">{region.flag}</Typography>
                    <Typography variant="h6">{region.region} Regulatory Requirements</Typography>
                  </Box>
                  
                  {region.regulations.map((regulation) => (
                    <Accordion key={regulation.id}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                          <Typography variant="subtitle1">{regulation.title}</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              label={regulation.type}
                              size="small"
                              color={regulation.type === 'mandatory' ? 'error' : regulation.type === 'upcoming' ? 'warning' : 'info'}
                            />
                            <Chip
                              label={regulation.status}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(regulation.status) + '20',
                                color: getStatusColor(regulation.status)
                              }}
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Description
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {regulation.description}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                              Authority
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {regulation.authority}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Implementation Deadline
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {formatDate(regulation.implementationDeadline)}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                              Compliance Rate
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={regulation.complianceRate}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="body2">
                                {regulation.complianceRate.toFixed(1)}%
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                              Penalties
                            </Typography>
                            <Typography variant="body2">
                              {regulation.penalties}
                            </Typography>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Regulatory Alerts ({regulatoryAlerts.length})
            </Typography>
            
            <Grid container spacing={3}>
              {regulatoryAlerts.map((alert) => (
                <Grid item xs={12} key={alert.id}>
                  <Card variant="outlined" sx={{
                    borderLeft: `4px solid ${getSeverityColor(alert.severity)}`
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: getSeverityColor(alert.severity) + '20',
                            color: getSeverityColor(alert.severity)
                          }}>
                            {alert.type === 'new-regulation' ? <Add /> :
                             alert.type === 'deadline-approaching' ? <Schedule /> :
                             alert.type === 'compliance-drop' ? <TrendingDown /> : <Warning />}
                          </Box>
                          <Box>
                            <Typography variant="h6" component="div">
                              {alert.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {alert.region} • {alert.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={alert.severity}
                            size="small"
                            sx={{
                              backgroundColor: getSeverityColor(alert.severity) + '20',
                              color: getSeverityColor(alert.severity)
                            }}
                          />
                          <Chip
                            label={alert.status}
                            size="small"
                            color={alert.status === 'resolved' ? 'success' : alert.status === 'acknowledged' ? 'warning' : 'default'}
                          />
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Deadline: {formatDate(alert.deadline)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Responsible: {alert.responsibleTeam}
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Action Required:</strong> {alert.actionRequired}
                        </Typography>
                      </Alert>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined">
                          Acknowledge
                        </Button>
                        <Button size="small" variant="outlined">
                          Assign Team
                        </Button>
                        <Button size="small" variant="contained">
                          Take Action
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <Dialog open={alertDialogOpen} onClose={() => setAlertDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Regulatory Alert Management</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Manage and respond to regulatory alerts across regions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={regulationDialogOpen}
        onClose={() => {
          setRegulationDialogOpen(false);
          resetRegulationForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Gavel />
            <Box>
              <Typography variant="h6">
                Add New Regulatory Requirement
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a new compliance regulation for a specific region
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Region Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Region</InputLabel>
                <Select
                  value={newRegulation.region}
                  label="Region"
                  onChange={(e) => setNewRegulation({
                    ...newRegulation,
                    region: e.target.value
                  })}
                >
                  <MenuItem value="Lagos">🇳🇬 Lagos, Nigeria</MenuItem>
                  <MenuItem value="Nairobi">🇰🇪 Nairobi, Kenya</MenuItem>
                  <MenuItem value="Accra">🇬🇭 Accra, Ghana</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Regulation Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Regulation Title"
                value={newRegulation.title}
                onChange={(e) => setNewRegulation({
                  ...newRegulation,
                  title: e.target.value
                })}
                placeholder="e.g., Lagos State E-hailing Regulation 2024"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Description"
                value={newRegulation.description}
                onChange={(e) => setNewRegulation({
                  ...newRegulation,
                  description: e.target.value
                })}
                placeholder="Brief description of the regulatory requirement"
              />
            </Grid>

            {/* Type and Deadline */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Regulation Type</InputLabel>
                <Select
                  value={newRegulation.type}
                  label="Regulation Type"
                  onChange={(e) => setNewRegulation({
                    ...newRegulation,
                    type: e.target.value as 'mandatory' | 'recommended' | 'upcoming'
                  })}
                >
                  <MenuItem value="mandatory">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Error sx={{ color: '#F44336' }} />
                      Mandatory
                    </Box>
                  </MenuItem>
                  <MenuItem value="recommended">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: '#2196F3' }} />
                      Recommended
                    </Box>
                  </MenuItem>
                  <MenuItem value="upcoming">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ color: '#FF9800' }} />
                      Upcoming
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Implementation Deadline"
                value={newRegulation.implementationDeadline}
                onChange={(e) => setNewRegulation({
                  ...newRegulation,
                  implementationDeadline: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Authority */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Regulatory Authority"
                value={newRegulation.authority}
                onChange={(e) => setNewRegulation({
                  ...newRegulation,
                  authority: e.target.value
                })}
                placeholder="e.g., Lagos State Transport Authority"
              />
            </Grid>

            {/* Penalties */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Penalties for Non-Compliance"
                value={newRegulation.penalties}
                onChange={(e) => setNewRegulation({
                  ...newRegulation,
                  penalties: e.target.value
                })}
                placeholder="e.g., N500,000 - N2,000,000 fine"
              />
            </Grid>

            {/* Affected Hosts */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Number of Affected Hosts"
                value={newRegulation.affectedHosts}
                onChange={(e) => setNewRegulation({
                  ...newRegulation,
                  affectedHosts: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
                helperText="Estimate of how many hosts will be affected by this regulation"
              />
            </Grid>

            {/* Info Alert */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  This regulation will be added to the selected region's compliance requirements. All affected hosts will be notified.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRegulationDialogOpen(false);
            resetRegulationForm();
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddRegulation}
            startIcon={<Add />}
          >
            Add Regulation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regional Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedRegionDetails(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        {selectedRegionDetails && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h4">{selectedRegionDetails.flag}</Typography>
                  <Box>
                    <Typography variant="h5">
                      {selectedRegionDetails.region}, {selectedRegionDetails.country}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Comprehensive Regional Compliance Report
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={selectedRegionDetails.riskLevel}
                  sx={{
                    backgroundColor: getRiskColor(selectedRegionDetails.riskLevel) + '20',
                    color: getRiskColor(selectedRegionDetails.riskLevel),
                    textTransform: 'capitalize',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Overview Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assessment /> Compliance Overview
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Overall Compliance Rate
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h3" component="div">
                            {selectedRegionDetails.overallCompliance.toFixed(1)}%
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {selectedRegionDetails.trendDirection === 'up' ? (
                              <TrendingUp sx={{ color: '#4CAF50', fontSize: 30 }} />
                            ) : (
                              <TrendingDown sx={{ color: '#F44336', fontSize: 30 }} />
                            )}
                            <Typography
                              variant="h6"
                              sx={{
                                color: selectedRegionDetails.trendDirection === 'up' ? '#4CAF50' : '#F44336'
                              }}
                            >
                              {selectedRegionDetails.trend > 0 ? '+' : ''}{selectedRegionDetails.trend}%
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={selectedRegionDetails.overallCompliance}
                          sx={{ height: 10, borderRadius: 5, mt: 2 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Active Hosts
                        </Typography>
                        <Typography variant="h3" component="div">
                          {selectedRegionDetails.activeHosts.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          out of {selectedRegionDetails.totalHosts.toLocaleString()} total
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(selectedRegionDetails.activeHosts / selectedRegionDetails.totalHosts) * 100}
                          sx={{ height: 10, borderRadius: 5, mt: 2 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Risk Level
                        </Typography>
                        <Typography
                          variant="h3"
                          component="div"
                          sx={{
                            color: getRiskColor(selectedRegionDetails.riskLevel),
                            textTransform: 'capitalize'
                          }}
                        >
                          {selectedRegionDetails.riskLevel}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Next Audit: {formatDate(selectedRegionDetails.nextAudit)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Document Compliance Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment /> Document Compliance Breakdown
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      {Object.entries(selectedRegionDetails.documents).map(([key, doc]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2">{doc.localName}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6">{doc.rate.toFixed(1)}%</Typography>
                                {doc.required && (
                                  <Chip label="Required" size="small" color="error" />
                                )}
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={doc.rate}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: doc.rate >= 80 ? '#4CAF50' : doc.rate >= 60 ? '#FF9800' : '#F44336'
                                }
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>

              {/* Regulatory Requirements Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Policy /> Active Regulatory Requirements
                </Typography>
                {selectedRegionDetails.regulations.map((regulation) => (
                  <Card key={regulation.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {regulation.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {regulation.description}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                          <Chip
                            label={regulation.type}
                            size="small"
                            color={
                              regulation.type === 'mandatory' ? 'error' :
                              regulation.type === 'upcoming' ? 'warning' : 'info'
                            }
                          />
                          <Chip
                            label={regulation.status}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(regulation.status) + '20',
                              color: getStatusColor(regulation.status),
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Authority
                          </Typography>
                          <Typography variant="body2">
                            {regulation.authority}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Deadline
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(regulation.implementationDeadline)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Affected Hosts
                          </Typography>
                          <Typography variant="body2">
                            {regulation.affectedHosts.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Compliance Rate
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={regulation.complianceRate}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="body2">
                              {regulation.complianceRate.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>Penalties:</strong> {regulation.penalties}
                            </Typography>
                          </Alert>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Management Information */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person /> Management & Audit Information
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#2196F3' }}>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Compliance Officer
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {selectedRegionDetails.complianceOfficer}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#4CAF50' }}>
                            <Schedule />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Next Scheduled Audit
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(selectedRegionDetails.nextAudit)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#FF9800' }}>
                            <Update />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Last Updated
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(selectedRegionDetails.lastUpdated)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#9C27B0' }}>
                            <LocationOn />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Region
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {selectedRegionDetails.region}, {selectedRegionDetails.country}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setDetailsDialogOpen(false);
                setSelectedRegionDetails(null);
              }}>
                Close
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={handleExportReport}>
                Export Report
              </Button>
              <Button variant="contained" startIcon={<Edit />} onClick={handleEditDetails}>
                Edit Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Details Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditFormData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        {editFormData && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Edit />
                <Box>
                  <Typography variant="h6">
                    Edit Regional Compliance Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editFormData.flag} {editFormData.region}, {editFormData.country}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Compliance Officer"
                    value={editFormData.complianceOfficer}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      complianceOfficer: e.target.value
                    })}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Risk Level</InputLabel>
                    <Select
                      value={editFormData.riskLevel}
                      label="Risk Level"
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        riskLevel: e.target.value as 'low' | 'medium' | 'high' | 'critical'
                      })}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Overall Compliance Rate (%)"
                    type="number"
                    value={editFormData.overallCompliance}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      overallCompliance: parseFloat(e.target.value)
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Trend (%)"
                    type="number"
                    value={editFormData.trend}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      trend: parseFloat(e.target.value)
                    })}
                    inputProps={{ step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Hosts"
                    type="number"
                    value={editFormData.totalHosts}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      totalHosts: parseInt(e.target.value)
                    })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Active Hosts"
                    type="number"
                    value={editFormData.activeHosts}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      activeHosts: parseInt(e.target.value)
                    })}
                    inputProps={{ min: 0, max: editFormData.totalHosts }}
                  />
                </Grid>

                {/* Document Compliance Rates */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Document Compliance Rates (%)
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${editFormData.documents.identity.localName} (%)`}
                    type="number"
                    value={editFormData.documents.identity.rate}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      documents: {
                        ...editFormData.documents,
                        identity: {
                          ...editFormData.documents.identity,
                          rate: parseFloat(e.target.value)
                        }
                      }
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${editFormData.documents.license.localName} (%)`}
                    type="number"
                    value={editFormData.documents.license.rate}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      documents: {
                        ...editFormData.documents,
                        license: {
                          ...editFormData.documents.license,
                          rate: parseFloat(e.target.value)
                        }
                      }
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${editFormData.documents.vehicle.localName} (%)`}
                    type="number"
                    value={editFormData.documents.vehicle.rate}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      documents: {
                        ...editFormData.documents,
                        vehicle: {
                          ...editFormData.documents.vehicle,
                          rate: parseFloat(e.target.value)
                        }
                      }
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${editFormData.documents.insurance.localName} (%)`}
                    type="number"
                    value={editFormData.documents.insurance.rate}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      documents: {
                        ...editFormData.documents,
                        insurance: {
                          ...editFormData.documents.insurance,
                          rate: parseFloat(e.target.value)
                        }
                      }
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${editFormData.documents.businessLicense.localName} (%)`}
                    type="number"
                    value={editFormData.documents.businessLicense.rate}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      documents: {
                        ...editFormData.documents,
                        businessLicense: {
                          ...editFormData.documents.businessLicense,
                          rate: parseFloat(e.target.value)
                        }
                      }
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${editFormData.documents.taxClearance.localName} (%)`}
                    type="number"
                    value={editFormData.documents.taxClearance.rate}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      documents: {
                        ...editFormData.documents,
                        taxClearance: {
                          ...editFormData.documents.taxClearance,
                          rate: parseFloat(e.target.value)
                        }
                      }
                    })}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      Changes will be reflected immediately in the details view. In a production environment, this would save to the database.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setEditDialogOpen(false);
                setEditFormData(null);
              }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEditedDetails}>
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RegionalCompliancePage;